/**
 * BooksMediaPostForm - Multi-step posting wizard for Books & Media
 * Includes specialized forms for each subcategory with safety features
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  BookOpen, Newspaper, GraduationCap, BookImage, Disc, 
  Download, Gem, Pencil, ArrowLeft, ArrowRight, 
  Loader2, CheckCircle, AlertTriangle, Phone, MapPin,
  Truck, Store, DollarSign, ShieldAlert, Info
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = {
  'books': {
    name: 'Books',
    icon: BookOpen,
    color: 'from-blue-500 to-blue-600',
    fields: ['author', 'isbn', 'language', 'edition', 'book_category', 'year_published']
  },
  'magazines': {
    name: 'Magazines',
    icon: Newspaper,
    color: 'from-pink-500 to-rose-600',
    fields: ['publication_name', 'issue_date', 'issue_count', 'language', 'is_bundle']
  },
  'educational-media': {
    name: 'Educational Media',
    icon: GraduationCap,
    color: 'from-purple-500 to-violet-600',
    fields: ['subject', 'level', 'curriculum', 'includes_past_papers', 'exam_year']
  },
  'comics-graphic-novels': {
    name: 'Comics & Graphic Novels',
    icon: BookImage,
    color: 'from-amber-500 to-orange-600',
    fields: ['series_name', 'publisher', 'issue_number', 'language', 'collector_grade']
  },
  'cds-dvds': {
    name: 'CDs & DVDs',
    icon: Disc,
    color: 'from-cyan-500 to-blue-600',
    fields: ['format', 'genre', 'artist_or_title', 'item_count', 'region_code', 'is_box_set']
  },
  'digital-media': {
    name: 'Digital Media',
    icon: Download,
    color: 'from-red-500 to-rose-600',
    fields: ['digital_type', 'platform', 'delivery_method', 'license_owned', 'not_pirated'],
    requiresCompliance: true
  },
  'collectibles': {
    name: 'Collectibles',
    icon: Gem,
    color: 'from-emerald-500 to-teal-600',
    fields: ['item_type', 'author', 'era', 'is_signed', 'has_certificate', 'provenance']
  },
  'stationery': {
    name: 'Stationery',
    icon: Pencil,
    color: 'from-gray-500 to-slate-600',
    fields: ['item_type', 'brand', 'is_bundle', 'item_count', 'suitable_for']
  }
};

// Category selector
const CategorySelector = ({ selected, onSelect, disabledCategories = [] }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {Object.entries(CATEGORIES).map(([slug, config]) => {
      const Icon = config.icon;
      const isDisabled = disabledCategories.includes(slug);
      return (
        <Card 
          key={slug}
          className={`cursor-pointer transition-all ${
            isDisabled ? 'opacity-50 cursor-not-allowed' :
            selected === slug ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:shadow-lg hover:bg-gray-50'
          }`}
          onClick={() => !isDisabled && onSelect(slug)}
          data-testid={`category-${slug}`}
        >
          <CardContent className="p-4 text-center">
            <div className={`w-12 h-12 mx-auto rounded-full bg-gradient-to-r ${config.color} flex items-center justify-center mb-2`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <p className="font-medium text-sm">{config.name}</p>
            {isDisabled && (
              <Badge variant="outline" className="mt-1 text-xs">Disabled</Badge>
            )}
            {config.requiresCompliance && (
              <Badge className="mt-1 text-xs bg-amber-500">Strict</Badge>
            )}
          </CardContent>
        </Card>
      );
    })}
  </div>
);

// Books Form
const BooksForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Author *</Label>
        <Input 
          placeholder="Author name"
          value={formData.author || ''}
          onChange={(e) => setFormData({...formData, author: e.target.value})}
        />
      </div>
      <div>
        <Label>ISBN (optional)</Label>
        <Input 
          placeholder="ISBN-10 or ISBN-13"
          value={formData.isbn || ''}
          onChange={(e) => setFormData({...formData, isbn: e.target.value})}
        />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>Language</Label>
        <Select value={formData.language || 'English'} onValueChange={(v) => setFormData({...formData, language: v})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="English">English</SelectItem>
            <SelectItem value="Sinhala">Sinhala</SelectItem>
            <SelectItem value="Tamil">Tamil</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Edition</Label>
        <Input 
          placeholder="e.g., First Edition"
          value={formData.edition || ''}
          onChange={(e) => setFormData({...formData, edition: e.target.value})}
        />
      </div>
      <div>
        <Label>Category</Label>
        <Select value={formData.book_category || 'other'} onValueChange={(v) => setFormData({...formData, book_category: v})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fiction">Fiction</SelectItem>
            <SelectItem value="non_fiction">Non-Fiction</SelectItem>
            <SelectItem value="self_help">Self-Help</SelectItem>
            <SelectItem value="textbook">Textbook</SelectItem>
            <SelectItem value="children">Children</SelectItem>
            <SelectItem value="reference">Reference</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

// Educational Media Form
const EducationalMediaForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Subject *</Label>
        <Input 
          placeholder="e.g., Mathematics, Physics"
          value={formData.subject || ''}
          onChange={(e) => setFormData({...formData, subject: e.target.value})}
        />
      </div>
      <div>
        <Label>Level *</Label>
        <Select value={formData.level || ''} onValueChange={(v) => setFormData({...formData, level: v})}>
          <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="primary">Primary School</SelectItem>
            <SelectItem value="secondary">Secondary School</SelectItem>
            <SelectItem value="o_level">O/L</SelectItem>
            <SelectItem value="a_level">A/L</SelectItem>
            <SelectItem value="university">University</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Curriculum</Label>
        <Select value={formData.curriculum || 'local'} onValueChange={(v) => setFormData({...formData, curriculum: v})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="local">Local</SelectItem>
            <SelectItem value="cambridge">Cambridge</SelectItem>
            <SelectItem value="edexcel">Edexcel</SelectItem>
            <SelectItem value="ib">IB</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Exam Year</Label>
        <Input 
          placeholder="e.g., 2025"
          value={formData.exam_year || ''}
          onChange={(e) => setFormData({...formData, exam_year: e.target.value})}
        />
      </div>
    </div>
    <div className="flex gap-4">
      <div className="flex items-center gap-2">
        <Checkbox 
          id="past_papers"
          checked={formData.includes_past_papers === true}
          onCheckedChange={(c) => setFormData({...formData, includes_past_papers: c})}
        />
        <Label htmlFor="past_papers">Includes Past Papers</Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox 
          id="includes_audio"
          checked={formData.includes_audio === true}
          onCheckedChange={(c) => setFormData({...formData, includes_audio: c})}
        />
        <Label htmlFor="includes_audio">Includes Audio/CD</Label>
      </div>
    </div>
  </div>
);

// Digital Media Form (with compliance)
const DigitalMediaForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <Alert className="bg-amber-50 border-amber-200">
      <ShieldAlert className="w-4 h-4 text-amber-600" />
      <AlertDescription className="text-amber-800">
        <strong>Digital Safety Notice:</strong> Only licensed links/codes are allowed. 
        No file uploads or copied content. Violations result in account suspension.
      </AlertDescription>
    </Alert>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Type *</Label>
        <Select value={formData.digital_type || ''} onValueChange={(v) => setFormData({...formData, digital_type: v})}>
          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ebook_link">E-book Link</SelectItem>
            <SelectItem value="audiobook_link">Audiobook Link</SelectItem>
            <SelectItem value="course_code">Course Code</SelectItem>
            <SelectItem value="subscription_transfer">Subscription Transfer</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Platform *</Label>
        <Select value={formData.platform || ''} onValueChange={(v) => setFormData({...formData, platform: v})}>
          <SelectTrigger><SelectValue placeholder="Select platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Amazon">Amazon</SelectItem>
            <SelectItem value="Audible">Audible</SelectItem>
            <SelectItem value="Coursera">Coursera</SelectItem>
            <SelectItem value="Udemy">Udemy</SelectItem>
            <SelectItem value="Kindle">Kindle</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    
    <div>
      <Label>Delivery Method</Label>
      <Select value={formData.delivery_method || 'code'} onValueChange={(v) => setFormData({...formData, delivery_method: v})}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="code">Code Transfer</SelectItem>
          <SelectItem value="link">Link Share</SelectItem>
          <SelectItem value="account_transfer">Account Transfer</SelectItem>
        </SelectContent>
      </Select>
    </div>
    
    {/* Compliance checkboxes - REQUIRED */}
    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
      <p className="font-semibold text-red-800 mb-3">Required Compliance Checkboxes:</p>
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <Checkbox 
            id="license_owned"
            checked={formData.license_owned === true}
            onCheckedChange={(c) => setFormData({...formData, license_owned: c})}
            className="mt-1"
          />
          <Label htmlFor="license_owned" className="text-sm">
            I confirm that I own the rights/license to transfer this digital content
          </Label>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox 
            id="not_pirated"
            checked={formData.not_pirated === true}
            onCheckedChange={(c) => setFormData({...formData, not_pirated: c})}
            className="mt-1"
          />
          <Label htmlFor="not_pirated" className="text-sm">
            I confirm this is NOT copied, pirated, or illegally obtained content
          </Label>
        </div>
      </div>
    </div>
  </div>
);

// Collectibles Form
const CollectiblesForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Item Type *</Label>
        <Select value={formData.item_type || ''} onValueChange={(v) => setFormData({...formData, item_type: v})}>
          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="rare_book">Rare Book</SelectItem>
            <SelectItem value="first_edition">First Edition</SelectItem>
            <SelectItem value="signed_copy">Signed Copy</SelectItem>
            <SelectItem value="vintage_media">Vintage Media</SelectItem>
            <SelectItem value="limited_edition">Limited Edition</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Author/Creator</Label>
        <Input 
          placeholder="Author or creator name"
          value={formData.author || ''}
          onChange={(e) => setFormData({...formData, author: e.target.value})}
        />
      </div>
    </div>
    <div>
      <Label>Era/Period</Label>
      <Input 
        placeholder="e.g., 1950s, Victorian Era"
        value={formData.era || ''}
        onChange={(e) => setFormData({...formData, era: e.target.value})}
      />
    </div>
    <div className="flex gap-4">
      <div className="flex items-center gap-2">
        <Checkbox 
          id="is_signed"
          checked={formData.is_signed === true}
          onCheckedChange={(c) => setFormData({...formData, is_signed: c})}
        />
        <Label htmlFor="is_signed">Signed by Author</Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox 
          id="has_certificate"
          checked={formData.has_certificate === true}
          onCheckedChange={(c) => setFormData({...formData, has_certificate: c})}
        />
        <Label htmlFor="has_certificate">Certificate of Authenticity</Label>
      </div>
    </div>
    
    <div>
      <Label>Provenance (History/Origin)</Label>
      <Textarea 
        placeholder="Describe the item's history and how you acquired it..."
        value={formData.provenance || ''}
        onChange={(e) => setFormData({...formData, provenance: e.target.value})}
        rows={3}
      />
      <p className="text-xs text-gray-500 mt-1">
        Provenance is required for high-value items and signed copies
      </p>
    </div>
  </div>
);

// CDs & DVDs Form
const CDsDVDsForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Format *</Label>
        <Select value={formData.format || ''} onValueChange={(v) => setFormData({...formData, format: v})}>
          <SelectTrigger><SelectValue placeholder="Select format" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cd">CD</SelectItem>
            <SelectItem value="dvd">DVD</SelectItem>
            <SelectItem value="bluray">Blu-ray</SelectItem>
            <SelectItem value="vinyl">Vinyl</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Genre</Label>
        <Input 
          placeholder="e.g., Rock, Action, Documentary"
          value={formData.genre || ''}
          onChange={(e) => setFormData({...formData, genre: e.target.value})}
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Artist/Title *</Label>
        <Input 
          placeholder="Artist name or movie title"
          value={formData.artist_or_title || ''}
          onChange={(e) => setFormData({...formData, artist_or_title: e.target.value})}
        />
      </div>
      <div>
        <Label>Item Count</Label>
        <Input 
          type="number"
          min="1"
          value={formData.item_count || 1}
          onChange={(e) => setFormData({...formData, item_count: parseInt(e.target.value) || 1})}
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Region Code (DVDs)</Label>
        <Select value={formData.region_code || ''} onValueChange={(v) => setFormData({...formData, region_code: v})}>
          <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Region 0 (All)</SelectItem>
            <SelectItem value="1">Region 1 (USA/Canada)</SelectItem>
            <SelectItem value="2">Region 2 (Europe/Japan)</SelectItem>
            <SelectItem value="3">Region 3 (Asia)</SelectItem>
            <SelectItem value="4">Region 4 (Australia/LatAm)</SelectItem>
            <SelectItem value="5">Region 5 (Asia/Africa)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end gap-4">
        <div className="flex items-center gap-2">
          <Checkbox 
            id="is_box_set"
            checked={formData.is_box_set === true}
            onCheckedChange={(c) => setFormData({...formData, is_box_set: c})}
          />
          <Label htmlFor="is_box_set">Box Set</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox 
            id="original_pressing"
            checked={formData.original_pressing === true}
            onCheckedChange={(c) => setFormData({...formData, original_pressing: c})}
          />
          <Label htmlFor="original_pressing">Original Pressing</Label>
        </div>
      </div>
    </div>
    
    {formData.format === 'dvd' && (
      <Alert>
        <Info className="w-4 h-4" />
        <AlertDescription className="text-sm">
          DVDs are region-coded. Make sure to specify the region so buyers know if it's compatible with their players.
        </AlertDescription>
      </Alert>
    )}
  </div>
);

// Magazines Form
const MagazinesForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Publication Name *</Label>
        <Input 
          placeholder="e.g., National Geographic, Vogue"
          value={formData.publication_name || ''}
          onChange={(e) => setFormData({...formData, publication_name: e.target.value})}
        />
      </div>
      <div>
        <Label>Issue Date</Label>
        <Input 
          placeholder="e.g., January 2024"
          value={formData.issue_date || ''}
          onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
        />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>Number of Issues</Label>
        <Input 
          type="number"
          min="1"
          value={formData.issue_count || 1}
          onChange={(e) => setFormData({...formData, issue_count: parseInt(e.target.value) || 1})}
        />
      </div>
      <div>
        <Label>Language</Label>
        <Select value={formData.language || 'English'} onValueChange={(v) => setFormData({...formData, language: v})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="English">English</SelectItem>
            <SelectItem value="Sinhala">Sinhala</SelectItem>
            <SelectItem value="Tamil">Tamil</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end">
        <div className="flex items-center gap-2">
          <Checkbox 
            id="is_bundle"
            checked={formData.is_bundle === true}
            onCheckedChange={(c) => setFormData({...formData, is_bundle: c})}
          />
          <Label htmlFor="is_bundle">Selling as Bundle</Label>
        </div>
      </div>
    </div>
  </div>
);

// Comics & Graphic Novels Form
const ComicsForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Series Name *</Label>
        <Input 
          placeholder="e.g., Batman, One Piece"
          value={formData.series_name || ''}
          onChange={(e) => setFormData({...formData, series_name: e.target.value})}
        />
      </div>
      <div>
        <Label>Publisher</Label>
        <Input 
          placeholder="e.g., DC Comics, Marvel"
          value={formData.publisher || ''}
          onChange={(e) => setFormData({...formData, publisher: e.target.value})}
        />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>Issue Number</Label>
        <Input 
          placeholder="e.g., #1, Vol. 5"
          value={formData.issue_number || ''}
          onChange={(e) => setFormData({...formData, issue_number: e.target.value})}
        />
      </div>
      <div>
        <Label>Language</Label>
        <Select value={formData.language || 'English'} onValueChange={(v) => setFormData({...formData, language: v})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="English">English</SelectItem>
            <SelectItem value="Japanese">Japanese</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Collector Grade</Label>
        <Select value={formData.collector_grade || ''} onValueChange={(v) => setFormData({...formData, collector_grade: v})}>
          <SelectTrigger><SelectValue placeholder="Select grade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mint">Mint</SelectItem>
            <SelectItem value="near_mint">Near Mint</SelectItem>
            <SelectItem value="very_fine">Very Fine</SelectItem>
            <SelectItem value="fine">Fine</SelectItem>
            <SelectItem value="good">Good</SelectItem>
            <SelectItem value="fair">Fair</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <Checkbox 
        id="is_first_edition"
        checked={formData.is_first_edition === true}
        onCheckedChange={(c) => setFormData({...formData, is_first_edition: c})}
      />
      <Label htmlFor="is_first_edition">First Edition / First Print</Label>
    </div>
  </div>
);

// Stationery Form
const StationeryForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Item Type *</Label>
        <Select value={formData.item_type || ''} onValueChange={(v) => setFormData({...formData, item_type: v})}>
          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="school_supplies">School Supplies</SelectItem>
            <SelectItem value="art_supplies">Art Supplies</SelectItem>
            <SelectItem value="office_supplies">Office Supplies</SelectItem>
            <SelectItem value="craft_supplies">Craft Supplies</SelectItem>
            <SelectItem value="notebooks">Notebooks & Journals</SelectItem>
            <SelectItem value="pens_pencils">Pens & Pencils</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Brand (if applicable)</Label>
        <Input 
          placeholder="e.g., Staedtler, Faber-Castell"
          value={formData.brand || ''}
          onChange={(e) => setFormData({...formData, brand: e.target.value})}
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Number of Items</Label>
        <Input 
          type="number"
          min="1"
          value={formData.item_count || 1}
          onChange={(e) => setFormData({...formData, item_count: parseInt(e.target.value) || 1})}
        />
      </div>
      <div>
        <Label>Suitable For</Label>
        <Select value={formData.suitable_for || ''} onValueChange={(v) => setFormData({...formData, suitable_for: v})}>
          <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="primary">Primary School</SelectItem>
            <SelectItem value="secondary">Secondary School</SelectItem>
            <SelectItem value="university">University</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="all">All Levels</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <Checkbox 
        id="is_stationery_bundle"
        checked={formData.is_bundle === true}
        onCheckedChange={(c) => setFormData({...formData, is_bundle: c})}
      />
      <Label htmlFor="is_stationery_bundle">Selling as Bundle/Set</Label>
    </div>
  </div>
);

// Generic form for simpler categories
const GenericForm = ({ formData, setFormData, category }) => (
  <div className="space-y-4">
    <Alert>
      <Info className="w-4 h-4" />
      <AlertDescription>
        Fill in the basic information above. Additional fields are optional.
      </AlertDescription>
    </Alert>
  </div>
);

// Main Component
export default function BooksMediaPostForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country } = useCountry();
  
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    condition: 'used',
    location: '',
    contact_phone: '',
    delivery_available: false,
    pickup_available: true,
    negotiable: true,
    images: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdListing, setCreatedListing] = useState(null);
  const [countryConfig, setCountryConfig] = useState(null);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  const categoryConfig = selectedCategory ? CATEGORIES[selectedCategory] : null;
  
  // Fetch country config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/books-media/config/${country}`);
        const data = await response.json();
        setCountryConfig(data.config);
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };
    fetchConfig();
  }, [country]);
  
  // Get disabled categories
  const disabledCategories = countryConfig 
    ? Object.entries(countryConfig.subcategories || {})
        .filter(([_, cfg]) => !cfg.enabled)
        .map(([slug]) => slug)
    : [];
  
  // Check if digital media is enabled
  const digitalMediaEnabled = countryConfig?.digital_media_enabled || false;
  if (!digitalMediaEnabled && !disabledCategories.includes('digital-media')) {
    disabledCategories.push('digital-media');
  }
  
  // Form validation
  const validateForm = () => {
    if (!formData.title || formData.title.length < 5) {
      setError('Title must be at least 5 characters');
      return false;
    }
    if (!formData.description || formData.description.length < 20) {
      setError('Description must be at least 20 characters');
      return false;
    }
    if (!formData.location) {
      setError('Location is required');
      return false;
    }
    if (!formData.contact_phone) {
      setError('Phone number is required');
      return false;
    }
    
    // Digital media compliance check
    if (selectedCategory === 'digital-media') {
      if (!formData.license_owned) {
        setError('You must confirm license ownership for digital media');
        return false;
      }
      if (!formData.not_pirated) {
        setError('You must confirm the content is not pirated');
        return false;
      }
    }
    
    return true;
  };
  
  // Submit form
  const handleSubmit = async () => {
    setError('');
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const endpoint = selectedCategory === 'digital-media' 
        ? '/api/books-media/listings/digital'
        : '/api/books-media/listings';
      
      const payload = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        category: selectedCategory,
        condition: formData.condition,
        location: formData.location,
        country: country,
        contact_phone: formData.contact_phone,
        delivery_available: formData.delivery_available,
        pickup_available: formData.pickup_available,
        negotiable: formData.negotiable,
        images: formData.images,
        extra_fields: { ...formData }
      };
      
      // For digital media, use flat structure
      if (selectedCategory === 'digital-media') {
        Object.assign(payload, {
          digital_type: formData.digital_type,
          platform: formData.platform,
          delivery_method: formData.delivery_method,
          license_owned: formData.license_owned,
          not_pirated: formData.not_pirated
        });
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create listing');
      }
      
      setCreatedListing(data);
      setSuccess(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Render category-specific form
  const renderCategoryForm = () => {
    switch (selectedCategory) {
      case 'books':
        return <BooksForm formData={formData} setFormData={setFormData} />;
      case 'magazines':
        return <MagazinesForm formData={formData} setFormData={setFormData} />;
      case 'educational-media':
        return <EducationalMediaForm formData={formData} setFormData={setFormData} />;
      case 'comics-graphic-novels':
        return <ComicsForm formData={formData} setFormData={setFormData} />;
      case 'cds-dvds':
        return <CDsDVDsForm formData={formData} setFormData={setFormData} />;
      case 'digital-media':
        return <DigitalMediaForm formData={formData} setFormData={setFormData} />;
      case 'collectibles':
        return <CollectiblesForm formData={formData} setFormData={setFormData} />;
      case 'stationery':
        return <StationeryForm formData={formData} setFormData={setFormData} />;
      default:
        return <GenericForm formData={formData} setFormData={setFormData} category={selectedCategory} />;
    }
  };
  
  // Success screen
  if (success && createdListing) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Listing Created!</h2>
              <p className="text-gray-600 mb-2">
                {createdListing.status === 'active' 
                  ? 'Your listing is now live.'
                  : 'Your listing is pending review and will be visible once approved.'}
              </p>
              {createdListing.status === 'pending' && (
                <Badge className="mb-4 bg-yellow-500">Pending Review</Badge>
              )}
              
              <div className="flex justify-center gap-4 mt-6">
                <Button onClick={() => navigate(`/category/${selectedCategory}`)}>
                  View Category
                </Button>
                <Button variant="outline" onClick={() => {
                  setSuccess(false);
                  setCreatedListing(null);
                  setFormData({
                    title: '', description: '', price: '', condition: 'used',
                    location: '', contact_phone: '', delivery_available: false,
                    pickup_available: true, negotiable: true, images: []
                  });
                  setStep(1);
                }}>
                  Create Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">Sell Books & Media</h1>
          <p className="text-gray-600">List your items for sale</p>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-20 h-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>
        
        {/* Step 1: Category Selection */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Category</CardTitle>
              <CardDescription>Choose the type of item you want to sell</CardDescription>
            </CardHeader>
            <CardContent>
              <CategorySelector 
                selected={selectedCategory} 
                onSelect={setSelectedCategory}
                disabledCategories={disabledCategories}
              />
              
              {selectedCategory === 'digital-media' && !digitalMediaEnabled && (
                <Alert className="mt-4" variant="destructive">
                  <ShieldAlert className="w-4 h-4" />
                  <AlertDescription>
                    Digital Media listings are currently disabled in your region. Contact support for more information.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!selectedCategory || disabledCategories.includes(selectedCategory)}
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 2: Item Details */}
        {step === 2 && categoryConfig && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-gradient-to-r ${categoryConfig.color}`}>
                  <categoryConfig.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>{categoryConfig.name}</CardTitle>
                  <CardDescription>Fill in the item details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Fields */}
              <div className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input 
                    placeholder="Clear, descriptive title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea 
                    placeholder="Describe your item in detail..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Price ({currencySymbol}) *</Label>
                    <Input 
                      type="number"
                      min="0"
                      placeholder="0 for free"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Condition</Label>
                    <Select value={formData.condition} onValueChange={(v) => setFormData({...formData, condition: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="like_new">Like New</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="used">Used</SelectItem>
                        <SelectItem value="worn">Worn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location *</Label>
                    <Input 
                      placeholder="City"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Phone *</Label>
                  <Input 
                    placeholder="Contact number"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  />
                </div>
                
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="pickup"
                      checked={formData.pickup_available}
                      onCheckedChange={(c) => setFormData({...formData, pickup_available: c})}
                    />
                    <Label htmlFor="pickup" className="flex items-center gap-1">
                      <Store className="w-4 h-4" /> Pickup
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="delivery"
                      checked={formData.delivery_available}
                      onCheckedChange={(c) => setFormData({...formData, delivery_available: c})}
                    />
                    <Label htmlFor="delivery" className="flex items-center gap-1">
                      <Truck className="w-4 h-4" /> Delivery
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="negotiable"
                      checked={formData.negotiable}
                      onCheckedChange={(c) => setFormData({...formData, negotiable: c})}
                    />
                    <Label htmlFor="negotiable">Price Negotiable</Label>
                  </div>
                </div>
              </div>
              
              {/* Category-Specific Fields */}
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-4">{categoryConfig.name} Details</h3>
                {renderCategoryForm()}
              </div>
              
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => setStep(3)}>
                  Preview
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 3: Review & Submit */}
        {step === 3 && categoryConfig && (
          <Card>
            <CardHeader>
              <CardTitle>Review Your Listing</CardTitle>
              <CardDescription>Make sure everything looks correct</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${categoryConfig.color}`}>
                    <categoryConfig.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <Badge className="mb-2">{categoryConfig.name}</Badge>
                    <h3 className="text-xl font-bold">{formData.title || 'Untitled'}</h3>
                    <p className="text-gray-600 mt-2">{formData.description || 'No description'}</p>
                    
                    <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                      <span className="font-bold text-blue-600 text-lg">
                        {currencySymbol}{formData.price || '0'}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {formData.location || 'No location'}
                      </span>
                      <Badge variant="outline">{formData.condition}</Badge>
                    </div>
                  </div>
                </div>
              </div>
              
              {selectedCategory === 'digital-media' && (
                <Alert className="bg-amber-50 border-amber-200">
                  <ShieldAlert className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    Digital Media listings require manual review before going live.
                  </AlertDescription>
                </Alert>
              )}
              
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Publish Listing
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
